import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../utils/locale';


const PHONE_PLACEHOLDERS = {
  GB: '+44 7700 900000',
  US: '+1 (555) 000-0000',
  IE: '+353 87 000 0000',
  AU: '+61 4XX XXX XXX',
  CA: '+1 (XXX) XXX-XXXX',
  NZ: '+64 21 XXX XXXX',
  ZA: '+27 71 000 0000',
  DE: '+49 170 0000000',
  FR: '+33 6 00 00 00 00',
  ES: '+34 600 000 000',
  IT: '+39 320 000 0000',
  NL: '+31 6 00000000',
  PT: '+351 91 000 0000',
  SE: '+46 70 000 0000',
  NO: '+47 40 00 00 00',
  DK: '+45 20 00 00 00',
  CH: '+41 78 000 0000',
  BE: '+32 470 00 00 00',
  AE: '+971 50 000 0000',
  SG: '+65 8000 0000',
};

function phonePlaceholder(country) {
  return PHONE_PLACEHOLDERS[country] || '+XX XXXX XXXXXX';
}

const DEFAULT_HOURS = [
  { day: 'Monday',    open: '06:00', close: '21:00', closed: false },
  { day: 'Tuesday',   open: '06:00', close: '21:00', closed: false },
  { day: 'Wednesday', open: '06:00', close: '21:00', closed: false },
  { day: 'Thursday',  open: '06:00', close: '21:00', closed: false },
  { day: 'Friday',    open: '06:00', close: '21:00', closed: false },
  { day: 'Saturday',  open: '08:00', close: '18:00', closed: false },
  { day: 'Sunday',    open: '08:00', close: '16:00', closed: true  },
];

const BASE = 'https://win.imagisoft.co.uk';

const THEMES = [
  {
    id: 1,
    name: 'Dark Warrior',
    desc: 'Bold black & red. Full-screen hero. Gym-inspired impact.',
    preview: { bg: '#0D0D0D', accent: '#D72638', text: '#fff' },
  },
  {
    id: 2,
    name: 'Clean Pro',
    desc: 'White & dark. Split hero. Professional studio aesthetic.',
    preview: { bg: '#fff', accent: '#D72638', text: '#1A1A2E' },
  },
  {
    id: 3,
    name: 'Premium Dark',
    desc: 'Near-black. Minimal nav. Services as a bold red grid.',
    preview: { bg: '#0A0A0A', accent: '#D72638', text: '#E8E8E8' },
  },
];

async function uploadFile(endpoint, fieldName, file) {
  const token = localStorage.getItem('ptm_token');
  const fd = new FormData();
  fd.append(fieldName, file);
  const res = await fetch('/api' + endpoint, {
    method: 'POST',
    headers: token ? { Authorization: 'Bearer ' + token } : {},
    body: fd,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || 'Upload failed');
  return data;
}

export default function LandingPageSettings() {
  const { trainer } = useAuth();
  const { t } = useLocale();
  const [page, setPage]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState(null);
  const heroRef                 = useRef();
  const galleryRef              = useRef();

  // Form state
  const [theme,        setTheme]        = useState(1);
  const [published,    setPublished]    = useState(false);
  const [slug,         setSlug]         = useState('');
  const [headline,     setHeadline]     = useState('Transform Your Body');
  const [subheadline,  setSubheadline]  = useState('Professional Personal Training');
  const [ctaText,      setCtaText]      = useState('Call Us Now');
  const [ctaPhone,     setCtaPhone]     = useState('');
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [aboutText,    setAboutText]    = useState('');
  const [services,     setServices]     = useState([]);
  const [newService,   setNewService]   = useState('');
  const [gallery,      setGallery]      = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [hours,        setHours]        = useState(DEFAULT_HOURS);
  const [phone,        setPhone]        = useState('');
  const [email,        setEmail]        = useState('');
  const [address,      setAddress]      = useState('');
  const [instagram,    setInstagram]    = useState('');
  const [facebook,     setFacebook]     = useState('');
  const [useBrandColors, setUseBrandColors] = useState(false);
  const [newTestiName, setNewTestiName] = useState('');
  const [newTestiQuote,setNewTestiQuote]= useState('');

  useEffect(() => {
    api.get('/settings/landing-page')
      .then(d => {
        setPage(d);
        setTheme(d.theme || 1);
        setPublished(d.published || false);
        setSlug(d.slug || '');
        setHeadline(d.headline || '');
        setSubheadline(d.subheadline || '');
        setCtaText(d.cta_text || '');
        setCtaPhone(d.cta_phone || '');
        setHeroImageUrl(d.hero_image_url || '');
        setAboutText(d.about_text || '');
        setServices(d.services || []);
        setGallery(d.gallery_images || []);
        setTestimonials(d.testimonials || []);
        setHours(d.hours && d.hours.length ? d.hours : DEFAULT_HOURS);
        setPhone(d.phone || '');
        setEmail(d.email || '');
        setAddress(d.address || '');
        setInstagram(d.instagram_url || '');
        setFacebook(d.facebook_url || '');
        setUseBrandColors(d.use_brand_colors || false);
      })
      .catch(() => {
        // No page yet — defaults are fine
      })
      .finally(() => setLoading(false));
  }, []);

  function flash(text, ok = true) {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 4000);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {
        theme, published, slug, headline, subheadline,
        cta_text: ctaText, cta_phone: ctaPhone,
        about_text: aboutText, services, hours,
        gallery_images: gallery, testimonials,
        phone, email, address,
        instagram_url: instagram, facebook_url: facebook,
        use_brand_colors: useBrandColors,
      };
      const result = page
        ? await api.post('/settings/landing-page', payload)
        : await api.post('/settings/landing-page', payload);
      setPage(result);
      setSlug(result.slug);
      flash('Landing page saved!');
    } catch (err) {
      flash(err.message || 'Save failed', false);
    } finally {
      setSaving(false);
    }
  }

  async function handleHeroUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const data = await uploadFile('/settings/landing-page/hero', 'hero', file);
      setHeroImageUrl(data.url);
      flash('Hero image uploaded!');
    } catch (err) {
      flash(err.message, false);
    }
  }

  async function handleGalleryUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const data = await uploadFile('/settings/landing-page/gallery', 'gallery', file);
      setGallery(data.gallery_images || []);
      flash('Gallery photo added!');
    } catch (err) {
      flash(err.message, false);
    }
  }

  async function handleRemoveGallery(idx) {
    try {
      const data = await api.delete('/settings/landing-page/gallery/' + idx);
      setGallery(data.gallery_images || []);
      flash('Photo removed');
    } catch (err) {
      flash(err.message, false);
    }
  }

  function addService() {
    const v = newService.trim();
    if (!v) return;
    setServices(s => [...s, v]);
    setNewService('');
  }

  function removeService(i) {
    setServices(s => s.filter((_, idx) => idx !== i));
  }

  function addTestimonial() {
    if (!newTestiName.trim() || !newTestiQuote.trim()) return;
    setTestimonials(t => [...t, { name: newTestiName.trim(), quote: newTestiQuote.trim() }]);
    setNewTestiName('');
    setNewTestiQuote('');
  }

  function removeTestimonial(i) {
    setTestimonials(t => t.filter((_, idx) => idx !== i));
  }

  function updateHour(i, field, val) {
    setHours(h => h.map((row, idx) => idx === i ? { ...row, [field]: val } : row));
  }

  if (loading) return (
    <div className="page-content">
      <div className="loading-spinner"><span className="spinner" /></div>
    </div>
  );

  const previewUrl = page?.slug ? BASE + '/' + page.slug : null;

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1>Landing Page</h1>
          <p style={{ color: 'var(--color-text-muted)', marginTop: 4, fontSize: 14 }}>
            Your public-facing page for clients to find and contact you.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {previewUrl && (
            <a href={previewUrl} target="_blank" rel="noopener noreferrer"
               style={{ fontSize: 13, color: 'var(--color-primary)', textDecoration: 'none', fontWeight: 600 }}>
              {published ? 'View Live Page ↗' : 'Preview ↗'}
            </a>
          )}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {msg && (
        <div style={{
          background: msg.ok ? 'rgba(46,204,113,0.1)' : 'rgba(215,38,56,0.1)',
          border: `1px solid ${msg.ok ? 'rgba(46,204,113,0.3)' : 'rgba(215,38,56,0.3)'}`,
          color: msg.ok ? '#2ecc71' : 'var(--color-primary)',
          borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 14,
        }}>
          {msg.text}
        </div>
      )}

      {/* ── THEME SELECTOR ── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 16, fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--color-text-muted)' }}>Choose Theme</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {THEMES.map(t => (
            <div key={t.id} onClick={() => setTheme(t.id)}
              style={{
                background: t.preview.bg,
                border: `2px solid ${theme === t.id ? 'var(--color-primary)' : 'var(--color-border)'}`,
                borderRadius: 10, padding: '20px 16px', cursor: 'pointer',
                transition: 'border-color 0.2s, transform 0.2s',
                transform: theme === t.id ? 'scale(1.02)' : 'scale(1)',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: t.preview.accent, flexShrink: 0 }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: t.preview.text }}>{t.name}</span>
              </div>
              <p style={{ fontSize: 12, color: t.preview.accent === '#D72638' && t.preview.bg === '#fff' ? '#666' : 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>{t.desc}</p>
              {theme === t.id && (
                <div style={{ marginTop: 10, fontSize: 11, fontWeight: 700, color: t.preview.accent, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Selected ✓
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── PUBLISH + SLUG ── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 16, fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--color-text-muted)' }}>Page Settings</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <div
              onClick={() => setPublished(v => !v)}
              style={{
                width: 44, height: 24, borderRadius: 12, background: published ? 'var(--color-primary)' : 'var(--color-border)',
                position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
              }}>
              <div style={{
                position: 'absolute', top: 3, left: published ? 22 : 3,
                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
              }} />
            </div>
            <span style={{ fontSize: 14, fontWeight: 600, color: published ? 'var(--color-primary)' : 'var(--color-text-muted)' }}>
              {published ? 'Published' : 'Unpublished'}
            </span>
          </label>
          {previewUrl && (
            <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
              URL: <a href={previewUrl} target="_blank" rel="noopener noreferrer"
                style={{ color: 'var(--color-primary)', fontWeight: 600 }}>
                win.imagisoft.co.uk/{page?.slug}
              </a>
            </span>
          )}
        </div>
        <div className="form-group">
          <label className="form-label">Page Slug (URL path)</label>
          <input className="form-input" value={slug} onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            placeholder="e.g. elite-pt" />
          <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4 }}>
            Must be unique. Will be auto-generated from your business name if left blank.
          </p>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', paddingTop: 4 }}>
          <input
            type="checkbox"
            checked={useBrandColors}
            onChange={e => setUseBrandColors(e.target.checked)}
            style={{ width: 16, height: 16, accentColor: 'var(--color-primary)', cursor: 'pointer', flexShrink: 0 }}
          />
          <span style={{ fontSize: 14, color: 'var(--color-text)' }}>
            {t('Use brand colors')}
            <span style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginTop: 2 }}>
              {t("Apply your brand color from Brand & Logo settings to this page's accent color")}
            </span>
          </span>
        </label>
      </div>

      {/* ── HERO ── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 16, fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--color-text-muted)' }}>Hero Section</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="form-group">
            <label className="form-label">Headline</label>
            <input className="form-input" value={headline} onChange={e => setHeadline(e.target.value)} placeholder="Transform Your Body" />
          </div>
          <div className="form-group">
            <label className="form-label">Subheadline</label>
            <input className="form-input" value={subheadline} onChange={e => setSubheadline(e.target.value)} placeholder="Professional Personal Training" />
          </div>
          <div className="form-group">
            <label className="form-label">CTA Button Text</label>
            <input className="form-input" value={ctaText} onChange={e => setCtaText(e.target.value)} placeholder="Call Us Now" />
          </div>
          <div className="form-group">
            <label className="form-label">CTA Phone Number</label>
            <input className="form-input" value={ctaPhone} onChange={e => setCtaPhone(e.target.value)} placeholder={phonePlaceholder(trainer?.country)} type="tel" />
          </div>
        </div>
        <div className="form-group" style={{ marginTop: 8 }}>
          <label className="form-label">Hero Background Image</label>
          {heroImageUrl && (
            <div style={{ marginBottom: 12, position: 'relative', display: 'inline-block' }}>
              <img src={heroImageUrl.startsWith('http') ? heroImageUrl : BASE + heroImageUrl}
                alt="Hero" style={{ width: 200, height: 120, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--color-border)' }} />
            </div>
          )}
          <div>
            <input type="file" accept="image/*" ref={heroRef} style={{ display: 'none' }} onChange={handleHeroUpload} />
            <button className="btn" onClick={() => heroRef.current?.click()} style={{ fontSize: 13 }}>
              {heroImageUrl ? 'Replace Hero Image' : 'Upload Hero Image'}
            </button>
            <span style={{ fontSize: 12, color: 'var(--color-text-muted)', marginLeft: 10 }}>Max 8MB. Saves immediately.</span>
          </div>
        </div>
      </div>

      {/* ── ABOUT ── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 16, fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--color-text-muted)' }}>About Section</h3>
        <div className="form-group">
          <label className="form-label">About Text</label>
          <textarea className="form-input" value={aboutText} onChange={e => setAboutText(e.target.value)}
            placeholder="Tell visitors about you, your training philosophy, and what makes you different..."
            style={{ minHeight: 120, resize: 'vertical' }} />
          <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4 }}>Leave blank to hide this section.</p>
        </div>
      </div>

      {/* ── SERVICES ── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 16, fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--color-text-muted)' }}>Services</h3>
        <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 16 }}>Add the services you offer. Theme 3 displays these as a bold red grid.</p>
        {services.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {services.map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 8, padding: '10px 14px' }}>
                <span style={{ flex: 1, fontSize: 14 }}>{s}</span>
                <button onClick={() => removeService(i)} style={{ background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <input className="form-input" value={newService} onChange={e => setNewService(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addService()}
            placeholder="e.g. 1-to-1 Personal Training" style={{ flex: 1, minWidth: 0 }} />
          <button className="btn btn-primary" onClick={addService}>Add</button>
        </div>
      </div>

      {/* ── GALLERY ── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 16, fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--color-text-muted)' }}>Gallery (up to 10 photos)</h3>
        <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 16 }}>
          {gallery.length}/10 photos. Uploads are saved immediately.
        </p>
        {gallery.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10, marginBottom: 16 }}>
            {gallery.map((url, i) => (
              <div key={i} style={{ position: 'relative' }}>
                <img src={url.startsWith('http') ? url : BASE + url} alt=""
                  style={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--color-border)' }} />
                <button onClick={() => handleRemoveGallery(i)}
                  style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.7)', border: 'none', color: '#fff', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        {gallery.length < 10 && (
          <div>
            <input type="file" accept="image/*" ref={galleryRef} style={{ display: 'none' }} onChange={handleGalleryUpload} />
            <button className="btn" onClick={() => galleryRef.current?.click()} style={{ fontSize: 13 }}>+ Add Photo</button>
          </div>
        )}
      </div>

      {/* ── TESTIMONIALS ── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 16, fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--color-text-muted)' }}>Testimonials</h3>
        {testimonials.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            {testimonials.map((t, i) => (
              <div key={i} style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{t.name}</div>
                    <div style={{ fontSize: 13, color: 'var(--color-text-muted)', fontStyle: 'italic' }}>"{t.quote}"</div>
                  </div>
                  <button onClick={() => removeTestimonial(i)} style={{ background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: 18, marginLeft: 10, lineHeight: 1 }}>×</button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
            <input className="form-input" value={newTestiName} onChange={e => setNewTestiName(e.target.value)} placeholder="Client name" />
            <input className="form-input" value={newTestiQuote} onChange={e => setNewTestiQuote(e.target.value)} placeholder="Their testimonial" />
          </div>
          <button className="btn btn-primary" onClick={addTestimonial} style={{ alignSelf: 'flex-start' }}>Add Testimonial</button>
        </div>
      </div>

      {/* ── HOURS ── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 16, fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--color-text-muted)' }}>Opening Hours</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {hours.map((h, i) => (
            <div key={h.day} style={{ display: 'grid', gridTemplateColumns: '100px auto auto auto', gap: 12, alignItems: 'center' }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>{h.day}</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={h.closed} onChange={e => updateHour(i, 'closed', e.target.checked)}
                  style={{ accentColor: 'var(--color-primary)' }} />
                <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Closed</span>
              </label>
              {!h.closed && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Open</span>
                    <input type="time" value={h.open} onChange={e => updateHour(i, 'open', e.target.value)}
                      style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '6px 8px', color: 'var(--color-text)', fontSize: 13 }} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Close</span>
                    <input type="time" value={h.close} onChange={e => updateHour(i, 'close', e.target.value)}
                      style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '6px 8px', color: 'var(--color-text)', fontSize: 13 }} />
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── CONTACT ── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 16, fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--color-text-muted)' }}>Contact Details</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-input" value={phone} onChange={e => setPhone(e.target.value)} placeholder={phonePlaceholder(trainer?.country)} type="tel" />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" value={email} onChange={e => setEmail(e.target.value)} placeholder="info@yoursite.com" type="email" />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Address</label>
          <input className="form-input" value={address} onChange={e => setAddress(e.target.value)} placeholder="123 High Street, City, Postcode" />
        </div>
      </div>

      {/* ── SOCIAL ── */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h3 style={{ marginBottom: 16, fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--color-text-muted)' }}>Social Media</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="form-group">
            <label className="form-label">Instagram URL</label>
            <input className="form-input" value={instagram} onChange={e => setInstagram(e.target.value)} placeholder="https://instagram.com/yourprofile" type="url" />
          </div>
          <div className="form-group">
            <label className="form-label">Facebook URL</label>
            <input className="form-input" value={facebook} onChange={e => setFacebook(e.target.value)} placeholder="https://facebook.com/yourpage" type="url" />
          </div>
        </div>
      </div>

      {/* ── SAVE BUTTON (bottom) ── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: 40 }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ minWidth: 160, padding: '14px 32px' }}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
